/* ============================================================================
   SK BOOKING PICKER (.skb) — shared view controller. Vanilla JS, no jQuery.
   ============================================================================

   Exposes ONE global, `window.SKBookingPicker`, with:

     SKBookingPicker.create(rootEl, options) -> picker
     SKBookingPicker.ready(fn)               -> run fn(SKBookingPicker) once
                                                the script has loaded (safe to
                                                call before OR after it does)

   ── THE BOUNDARY, AND WHY IT IS WHERE IT IS ───────────────────────────────
   This file renders a month grid and a row of time chips, and reports clicks.
   It performs NO network calls, knows NO endpoint, NO provider slug, NO
   duration, NO capacity, and — most importantly — NO line-item property names.

   That last one is deliberate. The property names on the cart line are the
   contract with supabase/functions/sk-massage-order-sync, they are hardcoded
   string literals there, and they differ between the massage form and the
   tutoring form. Keeping them in the host means adopting this picker can never
   silently rename one, which is the single change on these forms that breaks
   staff-visible booking data without breaking anything a test order would show.

   The host therefore keeps: fetching, timezone reasoning, notice rules,
   property writing, the Add-to-Cart guard. It gets: a calendar that looks and
   behaves the same on every product.

   ── DATES ARE STRINGS ─────────────────────────────────────────────────────
   Every date crossing this API is a 'YYYY-MM-DD' calendar date in the
   PROVIDER's timezone, never a Date object and never an instant. Lexicographic
   comparison on that format is chronological, so all ordering here is plain
   string comparison and no timezone can shift a day. The host computes what
   "today" is in the shop timezone and passes it in; this file never asks the
   device what day it is.

   ── OPTIONS ───────────────────────────────────────────────────────────────
     today   'YYYY-MM-DD'  shop-local today. Required — days before it render
                           as past.
     min     'YYYY-MM-DD'  earliest bookable date (defaults to `today`).
     max     'YYYY-MM-DD'  latest bookable date (defaults to today + 60d).
     emptyPanelText        panel copy before a date is chosen.
     dayFilter(ymd)        optional host predicate; return false to force a day
                           unavailable regardless of the availability map (the
                           tutoring form uses it for the `lesson-days:` tag).
     onMonth(from, to)     a month became visible; fetch its availability.
     onDay(ymd)            a bookable day was chosen; fetch its times.
     onSlot(value, ymd)    a time chip was chosen. `value` is the exact string
                           the host handed us in setSlots().
     onClear(reason)       the selection was dropped by the picker itself
                           ('bounds' = it fell outside min/max, 'month' = the
                           customer navigated away from it).

   ── METHODS ───────────────────────────────────────────────────────────────
     setBounds(min, max)             re-apply bounds; drops a stale selection
     setAvailability(map, unknown)   map: {'YYYY-MM-DD': count}
                                     unknown: 'off' (default) | 'open'
     setSlots(items)                 [{ value, label }] or ['3:30 PM', …]
     setSlotsMessage(text, kind)     kind: null | 'error'
     setPanelLabel(text)
     setCalendarMessage(text, kind)  kind: null | 'error'
     setSummary(text)                the green "chosen" confirmation row
     getDate() / getTime()
     clearTime() / clearSelection()
     focusGrid()                     scroll to + focus the calendar
   ============================================================================ */
(function () {
  'use strict';

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function ymd(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d); }
  function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }   // m is 1-12
  function firstWeekday(y, m) { return new Date(y, m - 1, 1).getDay(); } // 0 = Sunday

  /* Built from integer parts so the date is constructed in LOCAL time. Parsing
     'YYYY-MM-DD' with the Date constructor treats it as UTC, which renders the
     wrong weekday for every visitor west of Greenwich. */
  function partsOf(ymdStr) {
    var p = String(ymdStr).split('-');
    return { y: parseInt(p[0], 10), m: parseInt(p[1], 10), d: parseInt(p[2], 10) };
  }
  function weekdayOf(ymdStr) {
    var p = partsOf(ymdStr);
    return new Date(p.y, p.m - 1, p.d).getDay();
  }
  function dateLabel(ymdStr) {
    var p = partsOf(ymdStr);
    return DAY_NAMES[weekdayOf(ymdStr)] + ', ' + MONTHS[p.m - 1] + ' ' + p.d;
  }
  /* Calendar arithmetic in UTC so no local offset can shift the day. */
  function addDays(ymdStr, n) {
    var p = partsOf(ymdStr);
    var dt = new Date(Date.UTC(p.y, p.m - 1, p.d + n));
    return ymd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  }

  function Picker(root, opts) {
    opts = opts || {};

    this.root = root;
    this.opts = opts;
    this.today = opts.today || ymd(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());
    this.min = opts.min || this.today;
    this.max = opts.max || addDays(this.today, 60);
    this.emptyPanelText = opts.emptyPanelText || 'Select a date to see available times.';

    this.availability = {};
    this.unknownMode = 'off';
    this.selectedDate = null;
    this.selectedTime = null;
    this.cells = [];
    this.focusIndex = -1;

    var start = partsOf(this.min > this.today ? this.min : this.today);
    this.view = { y: start.y, m: start.m };

    this.el = {
      prev: root.querySelector('[data-skb-prev]'),
      next: root.querySelector('[data-skb-next]'),
      month: root.querySelector('[data-skb-month]'),
      grid: root.querySelector('[data-skb-grid]'),
      calState: root.querySelector('[data-skb-cal-state]'),
      panelLabel: root.querySelector('[data-skb-panel-label]'),
      slots: root.querySelector('[data-skb-slots]'),
      slotsState: root.querySelector('[data-skb-slots-state]'),
      selected: root.querySelector('[data-skb-selected]'),
      selectedText: root.querySelector('[data-skb-selected-text]')
    };

    var self = this;
    if (this.el.prev) {
      this.el.prev.addEventListener('click', function () { self.goMonth(-1, null); });
    }
    if (this.el.next) {
      this.el.next.addEventListener('click', function () { self.goMonth(1, null); });
    }

    this.renderMonth(null);
  }

  /* ── Classification ─────────────────────────────────────────────────────
     One place decides whether a day is bookable, so the paint, the click
     handler and the keyboard handler can never disagree about it. */
  Picker.prototype.classify = function (ymdStr) {
    if (ymdStr < this.today) return 'past';
    if (ymdStr < this.min) return 'off';        // inside the notice window
    if (ymdStr > this.max) return 'beyond';
    if (typeof this.opts.dayFilter === 'function' && !this.opts.dayFilter(ymdStr)) return 'off';
    if (Object.prototype.hasOwnProperty.call(this.availability, ymdStr)) {
      return this.availability[ymdStr] > 0 ? 'available' : 'off';
    }
    /* Not in the map. 'off' is the safe default (never advertise a day nobody
       confirmed). 'open' is for a host whose availability bootstrap failed and
       that would rather let the customer click and have /slots be the gate than
       show a page of grey. */
    return this.unknownMode === 'open' ? 'available' : 'off';
  };

  Picker.prototype.paint = function () {
    var self = this;
    this.cells.forEach(function (btn) {
      var key = btn.getAttribute('data-ymd');
      var cls = self.classify(key);
      btn.classList.remove('skb__cell--available', 'skb__cell--off', 'skb__cell--past', 'skb__cell--beyond');
      btn.classList.add('skb__cell--' + cls);

      var dot = btn.querySelector('.skb__cell-dot');
      if (cls === 'available') {
        if (!dot) {
          dot = document.createElement('span');
          dot.className = 'skb__cell-dot';
          btn.appendChild(dot);
        }
        btn.removeAttribute('aria-disabled');
        btn.setAttribute('aria-label', dateLabel(key) + ' — times available');
      } else {
        if (dot) dot.remove();
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-label', dateLabel(key) + (cls === 'past' ? ' — past' : ' — unavailable'));
      }
      btn.classList.toggle('skb__cell--selected', key === self.selectedDate);
      btn.setAttribute('aria-selected', key === self.selectedDate ? 'true' : 'false');
    });
  };

  /* ── Month rendering ────────────────────────────────────────────────── */
  Picker.prototype.monthBlocked = function (dir) {
    var minP = partsOf(this.min < this.today ? this.min : this.today);
    if (dir < 0) return this.view.y === minP.y && this.view.m === minP.m;
    var lastOfView = ymd(this.view.y, this.view.m, daysInMonth(this.view.y, this.view.m));
    return lastOfView >= this.max;
  };

  Picker.prototype.renderMonth = function (focusDir) {
    var self = this;
    this.el.month.textContent = MONTHS[this.view.m - 1] + ' ' + this.view.y;
    this.el.prev.disabled = this.monthBlocked(-1);
    this.el.next.disabled = this.monthBlocked(1);
    this.el.grid.innerHTML = '';
    this.cells = [];
    this.focusIndex = -1;

    var lead = firstWeekday(this.view.y, this.view.m);
    var total = daysInMonth(this.view.y, this.view.m);
    var i;

    for (i = 0; i < lead; i++) {
      var blank = document.createElement('div');
      blank.className = 'skb__cell skb__cell--blank';
      blank.setAttribute('aria-hidden', 'true');
      this.el.grid.appendChild(blank);
    }

    for (i = 1; i <= total; i++) {
      var key = ymd(this.view.y, this.view.m, i);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'skb__cell';
      btn.setAttribute('role', 'gridcell');
      btn.setAttribute('data-ymd', key);
      btn.setAttribute('tabindex', '-1');
      if (key === this.today) btn.classList.add('skb__cell--today');
      var span = document.createElement('span');
      span.textContent = String(i);
      btn.appendChild(span);
      btn.addEventListener('click', function (ev) { self.onDayClick(ev.currentTarget); });
      btn.addEventListener('keydown', function (ev) { self.onGridKeydown(ev); });
      this.el.grid.appendChild(btn);
      this.cells.push(btn);
    }

    this.paint();
    this.setRovingFocus(focusDir);

    if (typeof this.opts.onMonth === 'function') {
      this.opts.onMonth(ymd(this.view.y, this.view.m, 1), ymd(this.view.y, this.view.m, total));
    }
  };

  Picker.prototype.goMonth = function (delta, focusDir) {
    if (this.monthBlocked(delta)) return;
    var m = this.view.m + delta, y = this.view.y;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    this.view = { y: y, m: m };

    /* Navigating away from the chosen day drops the choice. Keeping it would
       leave a date on the order that the customer can no longer see, which is
       how somebody checks out believing they changed their mind. */
    if (this.selectedDate) {
      this.selectedDate = null;
      this.selectedTime = null;
      this.setSlots([]);
      this.setSlotsMessage('');
      this.setPanelLabel(this.emptyPanelText);
      this.setSummary('');
      if (typeof this.opts.onClear === 'function') this.opts.onClear('month');
    }
    this.renderMonth(focusDir);
  };

  /* ── Roving tabindex: the grid is ONE tab stop, arrows move within it ─── */
  Picker.prototype.isFocusable = function (cell) {
    return cell && !cell.classList.contains('skb__cell--blank');
  };

  Picker.prototype.setRovingFocus = function (focusDir) {
    var target = -1, i;
    if (focusDir === 'first') {
      for (i = 0; i < this.cells.length; i++) { if (this.isFocusable(this.cells[i])) { target = i; break; } }
    } else if (focusDir === 'last') {
      for (i = this.cells.length - 1; i >= 0; i--) { if (this.isFocusable(this.cells[i])) { target = i; break; } }
    } else {
      for (i = 0; i < this.cells.length; i++) {
        if (this.cells[i].classList.contains('skb__cell--available')) { target = i; break; }
      }
    }
    if (target < 0) target = focusDir === 'last' ? this.cells.length - 1 : 0;
    this.cells.forEach(function (c) { c.setAttribute('tabindex', '-1'); });
    if (this.cells[target]) {
      this.cells[target].setAttribute('tabindex', '0');
      this.focusIndex = target;
      if (focusDir === 'first' || focusDir === 'last') this.cells[target].focus();
    }
  };

  Picker.prototype.moveFocus = function (delta) {
    var nextIdx = this.focusIndex + delta;
    if (nextIdx < 0) { this.goMonth(-1, 'last'); return; }
    if (nextIdx >= this.cells.length) { this.goMonth(1, 'first'); return; }
    this.cells.forEach(function (c) { c.setAttribute('tabindex', '-1'); });
    this.cells[nextIdx].setAttribute('tabindex', '0');
    this.cells[nextIdx].focus();
    this.focusIndex = nextIdx;
  };

  Picker.prototype.onGridKeydown = function (ev) {
    switch (ev.key) {
      case 'ArrowRight': ev.preventDefault(); this.moveFocus(1); break;
      case 'ArrowLeft': ev.preventDefault(); this.moveFocus(-1); break;
      case 'ArrowDown': ev.preventDefault(); this.moveFocus(7); break;
      case 'ArrowUp': ev.preventDefault(); this.moveFocus(-7); break;
      case 'Home': ev.preventDefault(); this.setRovingFocus('first'); break;
      case 'End': ev.preventDefault(); this.setRovingFocus('last'); break;
      case 'PageUp': ev.preventDefault(); this.goMonth(-1, 'first'); break;
      case 'PageDown': ev.preventDefault(); this.goMonth(1, 'first'); break;
      case ' ':
      case 'Enter': ev.preventDefault(); this.onDayClick(ev.currentTarget); break;
      default: break;
    }
  };

  Picker.prototype.onDayClick = function (btn) {
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    if (!btn.classList.contains('skb__cell--available')) return;
    var key = btn.getAttribute('data-ymd');

    this.selectedDate = key;
    this.selectedTime = null;
    this.focusIndex = this.cells.indexOf(btn);
    this.cells.forEach(function (c) { c.setAttribute('tabindex', '-1'); });
    btn.setAttribute('tabindex', '0');
    this.paint();

    this.setSummary('');
    this.setPanelLabel(dateLabel(key));
    this.setSlots([]);

    if (typeof this.opts.onDay === 'function') this.opts.onDay(key);
  };

  /* ── Times ──────────────────────────────────────────────────────────── */
  Picker.prototype.setSlots = function (items) {
    var self = this;
    var slots = this.el.slots;
    if (!slots) return;
    slots.innerHTML = '';

    var list = (items || []).map(function (it) {
      if (typeof it === 'string') return { value: it, label: it };
      return { value: it.value, label: it.label != null ? it.label : it.value };
    });

    if (!list.length) { slots.hidden = true; return; }
    slots.hidden = false;

    list.forEach(function (item, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'skb__slot';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
      btn.setAttribute('data-value', item.value);
      btn.textContent = item.label;
      btn.addEventListener('click', function () { self.chooseSlot(item.value, btn); });
      btn.addEventListener('keydown', function (ev) { self.slotKeydown(ev, btn); });
      slots.appendChild(btn);
    });

    /* A background re-check that kept the customer's time re-renders the chips;
       re-assert the selection so it does not silently un-highlight. */
    if (this.selectedTime) {
      var keep = slots.querySelector('[data-value="' + String(this.selectedTime).replace(/"/g, '\\"') + '"]');
      if (keep) this.markSlot(keep);
      else this.selectedTime = null;
    }
  };

  Picker.prototype.markSlot = function (btn) {
    this.el.slots.querySelectorAll('.skb__slot').forEach(function (b) {
      b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
      b.setAttribute('tabindex', b === btn ? '0' : '-1');
    });
  };

  Picker.prototype.chooseSlot = function (value, btn) {
    this.selectedTime = value;
    this.markSlot(btn);
    if (typeof this.opts.onSlot === 'function') this.opts.onSlot(value, this.selectedDate);
  };

  Picker.prototype.slotKeydown = function (ev, btn) {
    var items = Array.prototype.slice.call(this.el.slots.querySelectorAll('.skb__slot'));
    var idx = items.indexOf(btn);
    var next = null;
    switch (ev.key) {
      case 'ArrowRight': case 'ArrowDown': next = items[idx + 1] || items[0]; break;
      case 'ArrowLeft': case 'ArrowUp': next = items[idx - 1] || items[items.length - 1]; break;
      case 'Home': next = items[0]; break;
      case 'End': next = items[items.length - 1]; break;
      case ' ': case 'Enter': ev.preventDefault(); btn.click(); return;
      default: return;
    }
    if (next) {
      ev.preventDefault();
      items.forEach(function (b) { b.setAttribute('tabindex', '-1'); });
      next.setAttribute('tabindex', '0');
      next.focus();
    }
  };

  /* ── Host-driven state ──────────────────────────────────────────────── */
  Picker.prototype.setAvailability = function (map, unknown) {
    this.availability = map || {};
    if (unknown) this.unknownMode = unknown;
    this.paint();
  };

  Picker.prototype.setBounds = function (min, max) {
    if (min) this.min = min;
    if (max) this.max = max;
    this.el.prev.disabled = this.monthBlocked(-1);
    this.el.next.disabled = this.monthBlocked(1);

    /* The notice boundary moves with the clock, so a date chosen at 3:59pm can
       be behind `min` at 4:01pm. Drop it rather than let it ride the order. */
    var dropped = false;
    if (this.selectedDate && (this.selectedDate < this.min || this.selectedDate > this.max)) {
      this.selectedDate = null;
      this.selectedTime = null;
      this.setSlots([]);
      this.setPanelLabel(this.emptyPanelText);
      this.setSummary('');
      dropped = true;
    }
    this.paint();
    if (dropped && typeof this.opts.onClear === 'function') this.opts.onClear('bounds');
  };

  Picker.prototype.setPanelLabel = function (text) {
    if (this.el.panelLabel) this.el.panelLabel.textContent = text;
  };

  Picker.prototype.setSlotsMessage = function (text, kind) {
    var n = this.el.slotsState;
    if (!n) return;
    n.className = 'skb__slots-state' + (kind ? ' skb__slots-state--' + kind : '');
    if (text) { n.textContent = text; n.hidden = false; }
    else { n.textContent = ''; n.hidden = true; }
  };

  Picker.prototype.setCalendarMessage = function (text, kind) {
    var n = this.el.calState;
    if (!n) return;
    n.className = 'skb__cal-state' + (kind ? ' skb__cal-state--' + kind : '');
    if (text) { n.textContent = text; n.hidden = false; }
    else { n.textContent = ''; n.hidden = true; }
  };

  Picker.prototype.setSummary = function (text) {
    if (!this.el.selected || !this.el.selectedText) return;
    if (text) { this.el.selectedText.textContent = text; this.el.selected.hidden = false; }
    else { this.el.selectedText.textContent = ''; this.el.selected.hidden = true; }
  };

  Picker.prototype.getDate = function () { return this.selectedDate; };
  Picker.prototype.getTime = function () { return this.selectedTime; };

  Picker.prototype.clearTime = function () {
    this.selectedTime = null;
    if (this.el.slots) {
      this.el.slots.querySelectorAll('.skb__slot').forEach(function (b) {
        b.setAttribute('aria-checked', 'false');
      });
    }
    this.setSummary('');
  };

  Picker.prototype.clearSelection = function () {
    this.selectedDate = null;
    this.selectedTime = null;
    this.setSlots([]);
    this.setSlotsMessage('');
    this.setPanelLabel(this.emptyPanelText);
    this.setSummary('');
    this.paint();
  };

  Picker.prototype.focusGrid = function () {
    if (!this.el.grid) return;
    try { this.el.grid.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch (e) { this.el.grid.scrollIntoView(); }
    var target = this.el.grid.querySelector('.skb__cell[tabindex="0"]');
    if (target) target.focus();
  };

  /* ── Public surface + a load-order-proof ready() ─────────────────────────
     Host snippets render an INLINE <script> in the product form, which runs
     before a `defer`red asset. `ready()` lets a host register its controller
     without caring which order the two landed in. */
  var api = {
    create: function (root, opts) { return root ? new Picker(root, opts) : null; },
    dateLabel: dateLabel,
    addDays: addDays,
    _queue: null
  };

  var pending = (window.SKBookingPicker && window.SKBookingPicker._queue) || [];
  api.ready = function (fn) { if (typeof fn === 'function') fn(api); };
  window.SKBookingPicker = api;
  pending.forEach(function (fn) { try { fn(api); } catch (e) { /* one host must not break the others */ } });
})();
